import { memo, useCallback, useMemo, useRef, useState } from 'react';
import type { RegistrationSlide } from '@/api/cms';
import { RegistrationForm } from '@/components/dialogs/Registration/components/RegistrationForm';
import { NativeSlider } from '@/components/NativeSlider';
import {
  SliderArrow,
  type SliderWithInsideArrows,
} from '@/components/NativeSlider/helpers';
import { useResizeObserver } from '@/hooks';
import { useGetCommonStore } from '@/store';
import { ScContainer } from '@/styles/common';
import { useSliderHeight } from '../../hooks';
import { RegBanner, type RegSlideProps } from './components/RegBanner';
import { ScRegFormAbsoluteWrapper } from './components/RegBanner/styled';
import { ScSliderContainer } from './styled';

const MARGIN_GAP = 24;
const DEKSTOP_GAP = 145;
const MOBILE_GAP = 505;

interface PromotionsSliderProps {
  regSlides: RegistrationSlide[];
}

export const RegistrationSlider = memo<PromotionsSliderProps>(
  ({ regSlides }) => {
    const elementRef = useRef<HTMLDivElement>(null);
    const [elementHeight, setElementHeight] = useState(0);

    const handleResize = useCallback((entry: ResizeObserverEntry) => {
      setElementHeight(entry.contentRect.height);
    }, []);

    useResizeObserver(elementRef, handleResize);

    const height = useSliderHeight();
    const useCommonStore = useGetCommonStore();
    const isMobileDevice = useCommonStore.use.isMobileDevice();

    const availableCrossSell = useCommonStore.use.availableCrossSell();

    const normalizedRegSlides = useMemo((): RegSlideProps[] => {
      if (!regSlides) return [];

      return regSlides.map(
        (
          {
            charactersDesktop,
            backgroundDesktop,
            backgroundMobile,
            charactersMobile,
            title,
            titleColor,
            benefits,
          },
          index
        ) => {
          const advantages =
            benefits?.map(({ text, crossSellText, icon }) => ({
              text: availableCrossSell && crossSellText ? crossSellText : text,
              icon: icon?.url || '',
            })) || [];

          const gap = isMobileDevice ? MOBILE_GAP : DEKSTOP_GAP;
          const isTooHigh = height - MARGIN_GAP - elementHeight - gap;
          const newHeight = elementHeight + MARGIN_GAP + gap;
          return {
            backgroundDesktop: backgroundDesktop?.url,
            backgroundMobile: backgroundMobile?.url,
            characterImage: isMobileDevice
              ? charactersMobile?.url
              : charactersDesktop?.url,
            title,
            titleColor,
            advantages,
            height: isTooHigh > 0 ? height : newHeight,
            priority: index === 0,
            loading: index === 0 ? 'eager' : 'lazy',
          };
        }
      );
    }, [regSlides, isMobileDevice, elementHeight, availableCrossSell, height]);

    const arrows: SliderWithInsideArrows = useMemo(
      () => ({
        type: SliderArrow.INSIDE,
        insetLeftArrow: isMobileDevice ? '191px auto auto 32px' : undefined,
        insetRightArrow: isMobileDevice ? '191px 32px auto auto' : undefined,
        visibleForMobile: true,
      }),
      [isMobileDevice]
    );

    return (
      <ScSliderContainer>
        <NativeSlider<RegSlideProps>
          arrows={arrows}
          autoplay={{
            delay: 8000,
            pauseOnMouseEnter: true,
          }}
          loop
          slide={RegBanner}
          slides={normalizedRegSlides}
        />
        <ScContainer>
          <ScRegFormAbsoluteWrapper ref={elementRef}>
            <RegistrationForm
              formId="one-step-reg"
              isOneStepRegistration
              isSliderShortForm
              openInnerModalsInPortal
            />
          </ScRegFormAbsoluteWrapper>
        </ScContainer>
      </ScSliderContainer>
    );
  }
);

export default RegistrationSlider;
